import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import { useRealtimeStore } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { sosApi } from '../services/api';
import ResponderPanel from '../components/sos/ResponderPanel';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  FunnelIcon,
} from '@heroicons/react/24/solid';

type SOSFilter = 'ALL' | 'TRIGGERED' | 'ACKNOWLEDGED' | 'RESOLVED' | 'CANCELLED';

export default function SOSCenterPage() {
  const queryClient = useQueryClient();
  const { sosEvents, selectedSOS, selectSOS } = useRealtimeStore();
  const { getEffectiveUnitIds, isHQ } = useUnitStore();
  const { user } = useAuthStore();
  const [acknowledgeNotes, setAcknowledgeNotes] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [filter, setFilter] = useState<SOSFilter>('ALL');

  // Get effective unit IDs for filtering
  // For unit users: use their unitId (locked to their unit)
  // For Brigade HQ: use selected units or all
  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const userIsHQ = isHQ(user?.unitId || null);
  // For unit users: always pass their unitId to API
  // For Brigade HQ (including 86ISB): pass unitId only if a specific unit is selected
  const unitIdForApi = userIsHQ
    ? (effectiveUnitIds && effectiveUnitIds.length === 1 ? effectiveUnitIds[0] : undefined)
    : (user?.unitId || undefined);
  
  // Filter SOS events by unit
  const filteredSOSEvents = useMemo(() => {
    if (!effectiveUnitIds) return sosEvents;
    return sosEvents.filter(sos => effectiveUnitIds.includes(sos.unitId || ''));
  }, [sosEvents, effectiveUnitIds]);

  // Get SOS history (all SOS events from backend)
  const { data: historyData } = useQuery({
    queryKey: ['sos-history-all', effectiveUnitIds],
    queryFn: () => sosApi.getHistory({ unitId: unitIdForApi }),
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      sosApi.acknowledge(id, notes),
    onSuccess: () => {
      toast.success('SOS acknowledged');
      queryClient.invalidateQueries({ queryKey: ['sos'] });
      queryClient.invalidateQueries({ queryKey: ['sos-history-all'] });
      setAcknowledgeNotes('');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Failed to acknowledge SOS';
      if (message.includes('not in TRIGGERED status')) {
        toast.error('SOS already acknowledged by another operator');
      } else {
        toast.error(message);
      }
    },
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      sosApi.resolve(id, notes),
    onSuccess: () => {
      toast.success('SOS resolved');
      queryClient.invalidateQueries({ queryKey: ['sos'] });
      queryClient.invalidateQueries({ queryKey: ['sos-history-all'] });
      selectSOS(null);
      setResolutionNotes('');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Failed to resolve SOS';
      toast.error(message);
    },
  });

  // Resolve ALL active SOS mutation ("All Clear")
  const resolveAllMutation = useMutation({
    mutationFn: async () => {
      const notes = `All clear by ${user?.firstName || 'Operator'} ${user?.lastName || ''}`.trim();
      try {
        // Preferred fast path (backend bulk endpoint, if available)
        return await sosApi.resolveAll({ unitId: unitIdForApi }, notes);
      } catch (e: any) {
        // Backward-compatible fallback: resolve each active SOS individually (works even if backend wasn't restarted yet)
        const status = e?.response?.status;
        if (status === 404) {
          const results = await Promise.allSettled(
            activeSOS.map((sos) => sosApi.resolve(sos.id, notes))
          );
          const resolved = results.filter((r) => r.status === 'fulfilled').length;
          return { data: { resolved } } as any;
        }
        throw e;
      }
    },
    onSuccess: (resp: any) => {
      const count = resp?.data?.resolved ?? 0;
      toast.success(count > 0 ? `All clear: resolved ${count} SOS` : 'No active SOS to clear');
      queryClient.invalidateQueries({ queryKey: ['sos'] });
      queryClient.invalidateQueries({ queryKey: ['sos-history-all'] });
      selectSOS(null);
      setAcknowledgeNotes('');
      setResolutionNotes('');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Failed to clear SOS';
      toast.error(message);
    },
  });

  // Combine realtime SOS events with history, filtered by unit
  const allSOSEvents = useMemo(() => {
    const combined = [
      ...filteredSOSEvents,
      ...(historyData?.data?.sosEvents || []).filter(
        (h: any) => !filteredSOSEvents.find((s) => s.id === h.id) &&
          (!effectiveUnitIds || effectiveUnitIds.includes(h.unitId || ''))
      ),
    ].sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime());
    return combined;
  }, [filteredSOSEvents, historyData?.data?.sosEvents, effectiveUnitIds]);

  // Filter SOS events by status
  const filteredSOS = useMemo(() => {
    return allSOSEvents.filter((sos) => {
      if (filter === 'ALL') return true;
      return sos.status === filter;
    });
  }, [allSOSEvents, filter]);

  const activeSOS = useMemo(() => {
    return filteredSOSEvents.filter(e => 
      ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(e.status)
    );
  }, [filteredSOSEvents]);
  
  const selectedEvent = allSOSEvents.find(e => e.id === selectedSOS);

  const handleAcknowledge = (id: string) => {
    acknowledgeMutation.mutate({ id, notes: acknowledgeNotes });
  };

  const handleResolve = (id: string) => {
    const notes = resolutionNotes.trim() ? resolutionNotes : undefined;
    resolveMutation.mutate({ id, notes });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TRIGGERED': return 'bg-red-500 text-white';
      case 'ACKNOWLEDGED': return 'bg-yellow-500 text-black';
      case 'RESPONDING': return 'bg-blue-500 text-white';
      case 'RESOLVED': return 'bg-green-500 text-white';
      case 'CANCELLED': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'TRIGGERED': return '🚨';
      case 'ACKNOWLEDGED': return '👁️';
      case 'RESPONDING': return '🚗';
      case 'RESOLVED': return '✅';
      case 'CANCELLED': return '❌';
      default: return '❓';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-bold text-gray-100">
            SOS Command Center
          </h1>
          <p className="text-gray-500 text-sm">
            Emergency response and management - All SOS Events
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {activeSOS.length > 0 && (
            <button
              onClick={() => {
                const scopeLabel = unitIdForApi ? 'this unit' : (userIsHQ ? 'ALL units' : 'your unit');
                if (!window.confirm(`Resolve ALL active SOS for ${scopeLabel}?`)) return;
                resolveAllMutation.mutate();
              }}
              disabled={resolveAllMutation.isPending}
              className={clsx(
                'px-4 py-2 rounded-lg font-bold transition-colors',
                'bg-green-600 hover:bg-green-700 text-white border border-green-500/40',
                resolveAllMutation.isPending && 'opacity-50 cursor-not-allowed'
              )}
              title="Resolve all active SOS (All Clear)"
            >
              {resolveAllMutation.isPending ? 'CLEARING...' : 'ALL CLEAR'}
            </button>
          )}
          <div className={clsx(
            'px-4 py-2 rounded-lg font-tactical text-lg',
            activeSOS.length > 0 
              ? 'bg-red-500/20 text-red-400 border-2 border-red-500 animate-pulse' 
              : 'bg-green-500/20 text-green-400 border border-green-500/30'
          )}>
            {activeSOS.length > 0 
              ? `${activeSOS.length} ACTIVE EMERGENCY` 
              : 'ALL CLEAR'}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <FunnelIcon className="w-5 h-5 text-gray-500" />
        {(['ALL', 'TRIGGERED', 'ACKNOWLEDGED', 'RESOLVED', 'CANCELLED'] as SOSFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filter === f
                ? 'bg-command-accent text-command-bg'
                : 'bg-command-panel border border-command-border text-gray-400 hover:text-white hover:border-gray-500'
            )}
          >
            {f === 'ALL' ? `All (${allSOSEvents.length})` : `${f} (${allSOSEvents.filter(s => s.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Fixed-height 3-column board on desktop; naturally stacked, per-panel scroll on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 lg:h-[calc(100vh-16rem)]">
        {/* SOS List - All Events */}
        <div className="bg-command-panel border border-command-border rounded-xl overflow-hidden flex flex-col max-h-[60dvh] lg:max-h-none">
          <div className="p-4 border-b border-command-border">
            <h2 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
              All SOS Events ({filteredSOS.length})
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {filteredSOS.map((sos) => (
              <div
                key={sos.id}
                onClick={() => selectSOS(sos.id)}
                className={clsx(
                  'p-4 rounded-lg border cursor-pointer transition-all',
                  selectedSOS === sos.id 
                    ? 'bg-blue-500/20 border-blue-500' 
                    : 'bg-command-bg border-command-border hover:border-gray-500',
                  sos.status === 'TRIGGERED' && 'border-red-500 bg-red-500/10 animate-pulse'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getStatusIcon(sos.status)}</span>
                    <div>
                      {(() => {
                        const platoon = sos.platoon;
                        const displayName = platoon?.callsign || 'Unknown platoon';
                        return (
                          <>
                            <p className="font-semibold text-gray-100 flex items-center gap-1">
                              <span>📍</span>
                              {displayName}
                            </p>
                            <p className="text-xs text-gray-500">
                              Platoon • {platoon?.unit?.code || 'N/A'}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <span className={clsx(
                    'px-2 py-1 rounded text-xs font-bold',
                    getStatusColor(sos.status)
                  )}>
                    {sos.status}
                  </span>
                </div>

                {/* Timeline */}
                <div className="mt-3 space-y-1 text-xs">
                  <div className="flex items-center gap-2 text-red-400">
                    <span>🚨</span>
                    <span>Triggered:</span>
                    <span className="text-gray-300">
                      {format(new Date(sos.triggeredAt), 'dd MMM yyyy HH:mm:ss')}
                    </span>
                  </div>
                  
                  {sos.acknowledgedAt && (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <span>👁️</span>
                      <span>Acknowledged:</span>
                      <span className="text-gray-300">
                        {format(new Date(sos.acknowledgedAt), 'HH:mm:ss')}
                        {sos.acknowledgedBy && ` by ${sos.acknowledgedBy.firstName || 'Operator'}`}
                      </span>
                    </div>
                  )}
                  
                  {sos.resolvedAt && (
                    <div className="flex items-center gap-2 text-green-400">
                      <span>✅</span>
                      <span>Resolved:</span>
                      <span className="text-gray-300">
                        {format(new Date(sos.resolvedAt), 'HH:mm:ss')}
                        {sos.resolvedBy && ` by ${sos.resolvedBy.firstName || 'Operator'}`}
                      </span>
                    </div>
                  )}

                  {sos.cancelledAt && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <span>❌</span>
                      <span>Cancelled:</span>
                      <span className="text-gray-300">
                        {format(new Date(sos.cancelledAt), 'HH:mm:ss')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                {sos.status === 'TRIGGERED' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAcknowledge(sos.id);
                    }}
                    disabled={acknowledgeMutation.isPending}
                    className="mt-3 w-full py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition-colors disabled:opacity-50"
                  >
                    ACKNOWLEDGE
                  </button>
                )}
              </div>
            ))}

            {filteredSOS.length === 0 && (
              <div className="text-center py-12">
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-400">No SOS events found</p>
                <p className="text-gray-600 text-sm">
                  {filter === 'ALL' ? 'No SOS events recorded yet' : `No ${filter.toLowerCase()} events`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Map & Details */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Map */}
          <div className="flex-1 bg-command-panel border border-command-border rounded-xl overflow-hidden min-h-[300px]">
            {selectedEvent ? (
              <MapContainer
                center={[selectedEvent.latitude, selectedEvent.longitude]}
                zoom={16}
                className="h-full w-full"
              >
                <TileLayer
                  url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                  attribution="&copy; Google Maps"
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                />
                <Circle
                  center={[selectedEvent.latitude, selectedEvent.longitude]}
                  radius={50}
                  pathOptions={{
                    color: '#ff4444',
                    fillColor: '#ff4444',
                    fillOpacity: 0.3,
                  }}
                />
                <Marker position={[selectedEvent.latitude, selectedEvent.longitude]} />
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <EyeIcon className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                  <p>Select an SOS event to view location</p>
                </div>
              </div>
            )}
          </div>

          {/* Details Panel */}
          {selectedEvent && (
            <div className="bg-command-panel border border-command-border rounded-xl p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  {getStatusIcon(selectedEvent.status)} SOS Event Details
                </h3>
                <button
                  onClick={() => selectSOS(null)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Platoon Info */}
                <div>
                  {(() => {
                    const platoon = selectedEvent.platoon;
                    const displayName = platoon?.callsign || 'Unknown platoon';
                    return (
                      <>
                        <h3 className="text-sm font-tactical text-gray-400 mb-3">PLATOON</h3>
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-command-bg rounded-full flex items-center justify-center text-2xl">
                            📍
                          </div>
                          <div>
                            <p className="font-semibold text-gray-100">
                              {displayName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {platoon?.unit?.code || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Event Timeline */}
                <div>
                  <h3 className="text-sm font-tactical text-gray-400 mb-3">EVENT TIMELINE</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3 p-2 bg-red-500/10 rounded-lg">
                      <span className="text-red-500 font-bold">🚨 TRIGGERED</span>
                      <span className="text-gray-300">
                        {format(new Date(selectedEvent.triggeredAt), 'dd MMM yyyy HH:mm:ss')}
                      </span>
                    </div>
                    
                    {selectedEvent.acknowledgedAt && (
                      <div className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded-lg">
                        <span className="text-yellow-500 font-bold">👁️ ACKNOWLEDGED</span>
                        <div className="text-gray-300">
                          <div>{format(new Date(selectedEvent.acknowledgedAt), 'dd MMM yyyy HH:mm:ss')}</div>
                          {selectedEvent.acknowledgedBy && (
                            <div className="text-xs text-gray-500">
                              By: {selectedEvent.acknowledgedBy.firstName} {selectedEvent.acknowledgedBy.lastName}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {selectedEvent.resolvedAt && (
                      <div className="flex items-center gap-3 p-2 bg-green-500/10 rounded-lg">
                        <span className="text-green-500 font-bold">✅ RESOLVED</span>
                        <div className="text-gray-300">
                          <div>{format(new Date(selectedEvent.resolvedAt), 'dd MMM yyyy HH:mm:ss')}</div>
                          {selectedEvent.resolvedBy && (
                            <div className="text-xs text-gray-500">
                              By: {selectedEvent.resolvedBy.firstName} {selectedEvent.resolvedBy.lastName}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedEvent.cancelledAt && (
                      <div className="flex items-center gap-3 p-2 bg-gray-500/10 rounded-lg">
                        <span className="text-gray-500 font-bold">❌ CANCELLED</span>
                        <span className="text-gray-300">
                          {format(new Date(selectedEvent.cancelledAt), 'dd MMM yyyy HH:mm:ss')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-command-border">
                <div className="text-center p-3 bg-command-bg rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Trigger Type</div>
                  <div className="font-bold text-white">{selectedEvent.triggerType}</div>
                </div>
                <div className="text-center p-3 bg-command-bg rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="font-mono text-sm text-white">
                    {selectedEvent.latitude.toFixed(5)}, {selectedEvent.longitude.toFixed(5)}
                  </div>
                </div>
                <div className="text-center p-3 bg-command-bg rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <div className={clsx('font-bold px-2 py-1 rounded inline-block', getStatusColor(selectedEvent.status))}>
                    {selectedEvent.status}
                  </div>
                </div>
              </div>

              {/* Responders (P6) */}
              <ResponderPanel sos={selectedEvent} />

              {/* Notes */}
              {(selectedEvent.acknowledgeNotes || selectedEvent.resolutionNotes) && (
                <div className="mt-6 pt-6 border-t border-command-border space-y-3">
                  {selectedEvent.acknowledgeNotes && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Acknowledgment Notes</div>
                      <div className="p-3 bg-yellow-500/10 rounded-lg text-gray-300 text-sm">
                        {selectedEvent.acknowledgeNotes}
                      </div>
                    </div>
                  )}
                  {selectedEvent.resolutionNotes && (
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Resolution Notes</div>
                      <div className="p-3 bg-green-500/10 rounded-lg text-gray-300 text-sm">
                        {selectedEvent.resolutionNotes}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {selectedEvent.status !== 'RESOLVED' && selectedEvent.status !== 'CANCELLED' && (
                <div className="mt-6 pt-6 border-t border-command-border">
                  {selectedEvent.status === 'TRIGGERED' && (
                    <div className="space-y-3">
                      <textarea
                        value={acknowledgeNotes}
                        onChange={(e) => setAcknowledgeNotes(e.target.value)}
                        placeholder="Add notes (optional)"
                        className="w-full px-4 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:border-command-accent"
                        rows={2}
                      />
                      <button
                        onClick={() => handleAcknowledge(selectedEvent.id)}
                        disabled={acknowledgeMutation.isPending}
                        className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition-colors disabled:opacity-50"
                      >
                        {acknowledgeMutation.isPending ? 'ACKNOWLEDGING...' : 'ACKNOWLEDGE EMERGENCY'}
                      </button>
                    </div>
                  )}

                  {(selectedEvent.status === 'ACKNOWLEDGED' || selectedEvent.status === 'RESPONDING') && (
                    <div className="space-y-3">
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="Enter resolution notes (required)"
                        className="w-full px-4 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:border-command-accent"
                        rows={3}
                      />
                      <button
                        onClick={() => handleResolve(selectedEvent.id)}
                        disabled={resolveMutation.isPending || !resolutionNotes.trim()}
                        className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors disabled:opacity-50"
                      >
                        {resolveMutation.isPending ? 'RESOLVING...' : 'MARK AS RESOLVED'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
